import Swiper from 'swiper/bundle'
import 'swiper/css/bundle'

/**
 * Carousels rendered by `_molecules/carouselWide` are initialised per rendered
 * block rather than by class, for two reasons:
 *
 * 1. Swiper resolves string navigation selectors with `document.querySelectorAll`
 *    (see modules/navigation.mjs), and carouselWide renders its arrows as siblings
 *    of the `.swiper` element rather than inside it — so `uniqueNavElements` can't
 *    narrow them down either. Two blocks sharing a handle on one page (e.g. the
 *    things-to-do and accommodation-highlights blocks both use `gallery`) bound the
 *    same arrows to both instances, so one click drove both. carouselWide wraps each
 *    block in `[data-carousel="<handle>"]`, so the arrows are looked up in there.
 *
 * 2. Loop mode needs `slidesPerView + loopedSlides` slides to re-order cleanly;
 *    Swiper only warns when it's short, then misbehaves. A block's slide count is
 *    only knowable per instance, so loop is switched off at the breakpoints where
 *    that particular block doesn't have the slides for it.
 *
 * Carousels from other templates still initialise by class below.
 */

// Mirrors the minimum Swiper's own loopFix checks before warning that loop mode
// won't work. Returns null when it can't be known up front (`auto` slide widths).
function minSlidesForLoop(params) {
    const { slidesPerView, centeredSlides, slidesOffsetBefore, slidesOffsetAfter } = params

    if (typeof slidesPerView !== 'number') return null

    const slidesPerGroup = params.slidesPerGroup ?? 1
    const bothDirections = Boolean(centeredSlides || slidesOffsetBefore || slidesOffsetAfter)

    let perView = Math.ceil(slidesPerView)
    if (bothDirections && perView % 2 === 0) perView += 1

    let looped = bothDirections ? Math.max(slidesPerGroup, Math.ceil(perView / 2)) : slidesPerGroup
    if (looped % slidesPerGroup !== 0) looped += slidesPerGroup - (looped % slidesPerGroup)
    looped += params.loopAdditionalSlides ?? 0

    return perView + looped
}

// Turns `loop` off at each breakpoint short of slides. Swiper applies breakpoint
// params by extending whatever is already in play, ascending, so the authored
// values accumulate the same way here and the result is stated explicitly at every
// level rather than relying on what carries forward.
function withLoopGuard(config, slideCount) {
    const canLoop = (params) => {
        const min = minSlidesForLoop(params)
        return min === null || slideCount >= min
    }

    let authored = { ...config }
    const guarded = { ...config, loop: Boolean(config.loop) && canLoop(authored) }
    const widths = Object.keys(config.breakpoints ?? {})

    if (!widths.length) return guarded

    guarded.breakpoints = {}

    for (const width of widths.map(Number).sort((a, b) => a - b)) {
        const breakpoint = config.breakpoints[width]
        authored = { ...authored, ...breakpoint }
        guarded.breakpoints[width] = {
            ...breakpoint,
            loop: Boolean(authored.loop) && canLoop(authored),
        }
    }

    return guarded
}

// `scopeFor` returns the element the arrows are looked up in, which differs by
// component: carouselWide renders them as siblings of `.swiper`, the banner
// galleries render them inside it.
function initScoped(selector, navHandle, config, scopeFor) {
    document.querySelectorAll(selector).forEach((el) => {
        const scope = scopeFor(el) ?? document
        const slideCount = el.querySelectorAll(':scope > .swiper-wrapper > .swiper-slide').length

        new Swiper(el, {
            ...withLoopGuard(config, slideCount),
            navigation: {
                prevEl: [...scope.querySelectorAll(`.prev-${navHandle}`)],
                nextEl: [...scope.querySelectorAll(`.next-${navHandle}`)],
            },
        })
    })
}

function initWideCarousels(handle, config) {
    initScoped(`.${handle}-carousel`, handle, config, (el) =>
        el.closest(`[data-carousel="${handle}"]`)
    )
}

new Swiper('.default-carousel', {
    slidesPerView: 1.25,
    spaceBetween: 24,
    loop: true,
    slidesOffsetBefore: 0,
    slidesOffsetAfter: 0,
    breakpoints: {
        768: {
            slidesPerView: 1.5,
            spaceBetween: 48,
        },
        1024: {
            slidesPerView: 2.25,
        },
        1280: {
            slidesPerView: 3,
            spaceBetween: 64,
        },
    },
    navigation: {
        prevEl: '.prev-slide',
        nextEl: '.next-slide',
    },
})

initWideCarousels('news', {
    slidesPerView: 1.15,
    spaceBetween: 16,
    loop: false,
    centeredSlides: true,
    breakpoints: {
        768: {
            slidesPerView: 2,
            spaceBetween: 16,
            loop: false,
            centeredSlides: false,
        },
        1366: {
            slidesPerView: 3,
            spaceBetween: 24,
            loop: true,
            centeredSlides: false,
        },
    },
})
new Swiper('.community-carousel', {
    slidesPerView: 1,
    spaceBetween: 16,
    loop: true,
    centeredSlides: true,
    breakpoints: {
        768: {
            slidesPerView: 2,
            spaceBetween: 16,
            centeredSlides: false,
        },
        1366: {
            slidesPerView: 3,
            spaceBetween: 24,
            centeredSlides: false,
        },
    },
    navigation: {
        prevEl: '.prev-community',
        nextEl: '.next-community',
    },
})
new Swiper('.communityMobile-carousel', {
    slidesPerView: 1,
    spaceBetween: 16,
    loop: true,
    centeredSlides: true,
    breakpoints: {
        768: {
            slidesPerView: 2,
            spaceBetween: 16,
            centeredSlides: false,
        },
        1366: {
            slidesPerView: 3,
            spaceBetween: 24,
            centeredSlides: false,
        },
    },
    navigation: {
        prevEl: '.prev-communityMobile',
        nextEl: '.next-communityMobile',
    },
})
new Swiper('.statsMobile-carousel', {
    slidesPerView: 1.15,
    spaceBetween: 16,
    loop: false,
    centeredSlides: true,
    navigation: {
        prevEl: '.prev-statsMobile',
        nextEl: '.next-statsMobile',
    },
})
initWideCarousels('social', {
    slidesPerView: 1.15,
    spaceBetween: 16,
    loop: true,
    centeredSlides: true,
    breakpoints: {
        768: {
            slidesPerView: 3,
            spaceBetween: 16,
            centeredSlides: false,
        },
        1366: {
            slidesPerView: 4.25,
            spaceBetween: 24,
            centeredSlides: false,
        },
    },
})
initWideCarousels('reviews', {
    slidesPerView: 1.15,
    spaceBetween: 16,
    loop: true,
    centeredSlides: true,
    breakpoints: {
        480: {
            slidesPerView: 'auto',
            spaceBetween: 16,
            centeredSlides: false,
        },
        768: {
            slidesPerView: 'auto',
            spaceBetween: 16,
            centeredSlides: false,
        },
        1366: {
            slidesPerView: 'auto',
            spaceBetween: 24,
            centeredSlides: false,
        },
    },
})
new Swiper('.location-carousel', {
    slidesPerView: 1.15,
    spaceBetween: 16,
    loop: false,
    centeredSlides: true,
    centerInsufficientSlides: true,
    breakpoints: {
        768: {
            slidesPerView: 2.5,
            spaceBetween: 16,
            centeredSlides: false,
        },
        1366: {
            slidesPerView: 3,
            spaceBetween: 24,
            centeredSlides: false,
        },
    },
    navigation: {
        prevEl: '.prev-location',
        nextEl: '.next-location',
    },
})
new Swiper('.property-carousel', {
    slidesPerView: 1,
    spaceBetween: 16,
    loop: false,
    breakpoints: {
        768: {
            slidesPerView: 2,
            spaceBetween: 24,
        },
    },
    navigation: {
        prevEl: '.prev-property',
        nextEl: '.next-property',
    },
})
new Swiper('.property-card-gallery-carousel', {
    slidesPerView: 1,
    spaceBetween: 16,
    loop: true,
    navigation: {
        prevEl: '.prev-property-card-gallery',
        nextEl: '.next-property-card-gallery',
    },
})
// Banner galleries (holidayParks + locations) keep their arrows inside the .swiper
// element, so that's the scope. One config covers all three class variants: they only
// ever differed by `initialSlide`, which was there to start on the second slide so the
// space to the left of the first wasn't empty. Loop mode gives every slide a left
// neighbour — at the start of the track that's the last slide — so it starts at 0 now.
// Loop needs 5 slides here (centeredSlides makes Swiper buffer both sides), and
// withLoopGuard drops back to a static track on galleries that don't have them.
initScoped(
    '.locations-gallery-carousel, .locations-gallery-carousel-alt, .locations-gallery-carousel-alt3',
    'locations-gallery',
    {
        slidesPerView: 1.15,
        spaceBetween: 16,
        loop: true,
        centeredSlides: true,
        breakpoints: {
            1366: {
                slidesPerView: 1.5,
                spaceBetween: 24,
                centeredSlides: true,
            },
        },
    },
    (el) => el
)
new Swiper('.property-gallery-carousel', {
    slidesPerView: 1,
    spaceBetween: 16,
    loop: true,
    centeredSlides: true,
    breakpoints: {
        1366: {
            slidesPerView: 1.5,
            spaceBetween: 24,
            centeredSlides: false,
        },
    },
    navigation: {
        prevEl: '.prev-property-gallery',
        nextEl: '.next-property-gallery',
    },
})


new Swiper('.logos-carousel', {
    slidesPerView: 'auto',
    spaceBetween: 4,
    loop: true,
    centeredSlides: false,
    breakpoints: {
        768: {
            spaceBetween: 12,
        },
    },
    navigation: {
        prevEl: '.prev-logos',
        nextEl: '.next-logos',
    },
})

new Swiper('.awards-carousel', {
    slidesPerView: 'auto',
    spaceBetween: 4,
    loop: true,
    centeredSlides: false,
    breakpoints: {
        768: {
            spaceBetween: 12,
        },
    },
    navigation: {
        prevEl: '.prev-awards',
        nextEl: '.next-awards',
    },
})


initWideCarousels('gallery', {
    slidesPerView: 1.15,
    spaceBetween: 16,
    loop: true,
    centeredSlides: false,
    breakpoints: {
        768: {
            slidesPerView: 2.25,
            spaceBetween: 16,
            centeredSlides: false,
        },
        1366: {
            slidesPerView: 3.5,
            spaceBetween: 24,
            centeredSlides: false,
        },
    },
})
